// ── Theme application ────────────────────────────────────────────────────────
// Applies a theme to the page (body class + CSS variables) without touching game state, so it can
// be switched mid-game. Only artwork listed in available.generated.json (written by build.mjs from
// what's actually in public/themes/) is referenced; anything not yet supplied falls back to the
// theme's built-in CSS look. Only the selected theme's images are referenced, so others never load.

import AVAILABLE from "./available.generated.json";
import { THEMES, DEFAULT_THEME, LEGACY_IDS, SHARED_COURT, themeById, type ThemeConfig, type CourtKey } from "./registry.ts";

const have = new Set<string>(AVAILABLE as string[]);
export const assetReady = (path?: string): path is string => !!path && have.has(path);

const THEME_KEY = "poli-theme";
const LEGACY_KEY = "poli-skin";

export function savedThemeId(): string {
  try {
    const v = localStorage.getItem(THEME_KEY) ?? LEGACY_IDS[localStorage.getItem(LEGACY_KEY) ?? ""];
    if (v && themeById(v)?.status === "ready") return v;
  } catch { /* storage unavailable */ }
  return DEFAULT_THEME;
}
let onThemeSaved: ((id: string) => void) | undefined;
/** Told whenever the player picks a theme (the account keeps it as a preference). */
export function setThemeSavedListener(fn: (id: string) => void): void { onThemeSaved = fn; }
export function saveThemeId(id: string, quiet = false): void {
  try { localStorage.setItem(THEME_KEY, id); } catch { /* ignore */ }
  if (!quiet) onThemeSaved?.(id);
}

let current: ThemeConfig = themeById(DEFAULT_THEME)!;
export const currentTheme = (): ThemeConfig => current;

// Image for a court card under the current theme: themed art first, then the shared deck.
export function courtImage(key: CourtKey): string | null {
  const own = current.courtArt?.[key];
  if (assetReady(own)) return own;
  return assetReady(SHARED_COURT[key]) ? SHARED_COURT[key] : null;
}

const url = (p: string) => `url("${p}")`;

// Each optional asset → (CSS variable, body flag class). The flag lets CSS switch from the
// built-in look to the artwork only when the file really exists.
export function applyTheme(id: string, target: HTMLElement = document.body): ThemeConfig {
  const t = themeById(id) ?? themeById(DEFAULT_THEME)!;
  if (target === document.body) current = t;
  for (const th of THEMES) target.classList.remove(th.cssClass);
  target.classList.add(t.cssClass);
  target.dataset.theme = t.id;
  const s = target.style;
  s.setProperty("--theme-primary", t.primaryColour);
  s.setProperty("--theme-secondary", t.secondaryColour);
  s.setProperty("--theme-accent", t.accentColour);
  s.setProperty("--theme-text", t.textColour);
  s.setProperty("--theme-bg-dim", String(t.backgroundDim ?? 0.5));
  const flags: [string, string | undefined, string][] = [
    ["--theme-bg-desktop", t.backgroundImage?.desktop, "has-bg"],
    ["--theme-felt", t.boardTexture, "has-felt"],
    ["--theme-rail", t.boardFrame, "has-rail"],
    ["--theme-card-back", t.cardBackImage, "has-card-back"],
    ["--theme-map", t.mapBackground, "has-map"],
    ["--theme-lobby", t.lobbyImage, "has-lobby"],
    ["--theme-panel", t.panelTexture, "has-panel"],
  ];
  for (const [prop, path, flag] of flags) {
    const ok = assetReady(path);
    target.classList.toggle(flag, ok);
    if (ok) s.setProperty(prop, url(path));
    else if (target !== document.body) s.setProperty(prop, "none");   // a preview tile mustn't inherit the page's artwork
    else s.removeProperty(prop);
  }
  // background crops: tablet / mobile fall back to the desktop image if not supplied
  const bg = t.backgroundImage;
  if (bg && assetReady(bg.desktop)) {
    s.setProperty("--theme-bg-tablet", url(assetReady(bg.tablet) ? bg.tablet : bg.desktop));
    s.setProperty("--theme-bg-mobile", url(assetReady(bg.mobile) ? bg.mobile : bg.desktop));
  } else { s.removeProperty("--theme-bg-tablet"); s.removeProperty("--theme-bg-mobile"); }
  target.classList.toggle("anim-lively", t.animationStyle === "lively");
  // the header shows the table's name (Classic keeps the game's own name)
  if (target === document.body) {
    const name = document.querySelector(".brand-name"), sub = document.querySelector(".brand-sub");
    if (name) name.textContent = t.id === "classic" ? "Poli Poker" : t.displayName;
    if (sub) sub.textContent = t.id === "classic" ? "Whiskey poker · fifty hands · fifty states" : "Poli Poker · fifty hands · fifty states";
  }
  // the lobby sign is lettered with the table's name
  s.setProperty("--lobby-title", JSON.stringify(t.displayName));
  return t;
}
